// @ts-nocheck
import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { trpcClient } from "@/lib/trpc";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ShieldCheck,
  Search,
  Truck,
  Box,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  ExternalLink,
  PackageCheck,
  Plus,
  Layers,
  Building2,
  Calendar,
  DollarSign,
  Filter,
  CheckSquare,
  ArrowRight,
  UserCheck,
  Check,
} from "lucide-react";
import { formatNumber, formatDate, formatMoney, cn } from "@/lib/utils";
import { toast } from "sonner";
import EntityLink from "@/components/shared/EntityLink";

interface FQCReadySelectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectBatch?: (batch: any) => void;
  onSelectBatches?: (batches: any[]) => void;
}

export default function FQCReadySelectModal({
  open,
  onOpenChange,
  onSelectBatch,
  onSelectBatches,
}: FQCReadySelectModalProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [customerFilter, setCustomerFilter] = useState<string>("ALL");
  const [selectedBatchIds, setSelectedBatchIds] = useState<string[]>([]);

  // Fetch batches with FQC PASSED/WARNING
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["fqc-ready-batches", searchTerm],
    queryFn: async () => {
      return await trpcClient.deliveries.getFqcReadyBatches.query({
        search: searchTerm || undefined,
      });
    },
    enabled: open,
  });

  const batches = data?.batches || [];

  // Filter batches by customer
  const filteredBatches = useMemo(() => {
    return batches.filter((b: any) => {
      if (customerFilter !== "ALL" && b.customerId !== customerFilter) {
        return false;
      }
      return true;
    });
  }, [batches, customerFilter]);

  // Unique customers list
  const customers = useMemo(() => {
    const map = new Map<string, { id: string; name: string; shortName: string }>();
    batches.forEach((b: any) => {
      if (b.customerId && !map.has(b.customerId)) {
        map.set(b.customerId, {
          id: b.customerId,
          name: b.customerName || b.customerShortName || "Khách hàng",
          shortName: b.customerShortName || b.customerName || "KH",
        });
      }
    });
    return Array.from(map.values());
  }, [batches]);

  // Summary Metrics (Rule 4 Single-Row Inline Metrics)
  const totalReadyQty = useMemo(() => {
    return filteredBatches.reduce((sum: number, b: any) => sum + (Number(b.qtyRemaining) || 0), 0);
  }, [filteredBatches]);

  const totalReadyValue = useMemo(() => {
    return filteredBatches.reduce(
      (sum: number, b: any) => sum + (Number(b.qtyRemaining) || 0) * (Number(b.unitPrice) || 0),
      0
    );
  }, [filteredBatches]);

  const totalAvailableCartons = useMemo(() => {
    return filteredBatches.reduce((sum: number, b: any) => sum + (b.cartonStats?.available || 0), 0);
  }, [filteredBatches]);

  const totalCartons = useMemo(() => {
    return filteredBatches.reduce((sum: number, b: any) => sum + (b.cartonStats?.total || 0), 0);
  }, [filteredBatches]);

  // Selection handlers (Rule 9 Bulk Actions)
  const allSelected =
    filteredBatches.length > 0 && selectedBatchIds.length === filteredBatches.length;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedBatchIds([]);
    } else {
      setSelectedBatchIds(filteredBatches.map((b: any) => b.qcFId));
    }
  };

  const toggleSelectRow = (qcFId: string) => {
    setSelectedBatchIds((prev) =>
      prev.includes(qcFId) ? prev.filter((id) => id !== qcFId) : [...prev, qcFId]
    );
  };

  const handleQuickCreate = (batch: any) => {
    if (onSelectBatch) {
      onSelectBatch(batch);
      onOpenChange(false);
      return;
    }
    onOpenChange(false);
    navigate({
      to: "/delivery/create",
      search: { fqcBatchIds: batch.qcFId },
    });
  };

  const handleBulkCreate = () => {
    if (selectedBatchIds.length === 0) return;
    const selectedBatches = batches.filter((b: any) => selectedBatchIds.includes(b.qcFId));
    // Một phiếu giao chỉ cho một khách hàng
    const customerNames = new Map<string, string>(
      selectedBatches.map((b: any) => [b.customerId || "?", b.customerShortName || b.customerName || "Chưa có khách hàng"]),
    );
    if (customerNames.size > 1) {
      toast.error(`Chỉ gom phiếu giao khi cùng khách hàng — đang chọn ${customerNames.size} khách hàng: ${Array.from(customerNames.values()).join(", ")}`);
      return;
    }

    if (onSelectBatches) {
      onSelectBatches(selectedBatches);
      onOpenChange(false);
      return;
    }

    if (onSelectBatch && selectedBatches.length === 1) {
      onSelectBatch(selectedBatches[0]);
      onOpenChange(false);
      return;
    }

    onOpenChange(false);
    navigate({
      to: "/delivery/create",
      search: { fqcBatchIds: selectedBatchIds.join(",") },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[96vw] max-w-[96vw] sm:max-w-[96vw] md:max-w-[96vw] lg:max-w-[96vw] xl:max-w-[96vw] 2xl:max-w-[1600px] h-[92vh] max-h-[95vh] flex flex-col p-0 overflow-hidden bg-white border-slate-200 shadow-2xl rounded-2xl">
        {/* ═══ 1. HEADER CHUẨN ENTERPRISE (Title + Inline Metrics) ═══ */}
        <DialogHeader className="shrink-0">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-emerald-100 text-emerald-800 rounded-xl shadow-xs shrink-0">
                <PackageCheck className="h-6 w-6 text-emerald-700" />
              </div>
              <div>
                <DialogTitle className="flex items-center gap-2">
                  Lô Thành Phẩm Đã Nghiệm Thu FQC (Sẵn Sàng Xuất Giao)
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300 font-mono text-xs">
                    {filteredBatches.length} Lô sẵn sàng
                  </Badge>
                </DialogTitle>
                <DialogDescription className="text-xs text-slate-500 mt-0.5">
                  Các Lệnh sản xuất có biên bản KCS Xuất xưởng Đạt (PASSED/WARNING) và còn số lượng cần giao cho khách hàng
                </DialogDescription>
              </div>
            </div>

            {/* Metrics Quick Bar */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 shadow-xs flex items-center gap-2 text-xs">
                <span className="text-slate-500">Tổng SL Chờ Giao:</span>
                <span className="font-bold text-emerald-700 tabular-nums font-mono text-sm">
                  {formatNumber(totalReadyQty)}
                </span>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 shadow-xs flex items-center gap-2 text-xs">
                <span className="text-slate-500">Giá Trị Dự Kiến:</span>
                <span className="font-bold text-indigo-700 tabular-nums text-sm">
                  {formatMoney(totalReadyValue)} ₫
                </span>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 shadow-xs flex items-center gap-2 text-xs">
                <span className="text-slate-500">Thùng Khả Dụng:</span>
                <span className="font-semibold text-slate-800 tabular-nums font-mono">
                  {totalAvailableCartons}/{totalCartons} Thùng
                </span>
              </div>
            </div>
          </div>

          {/* ═══ 2. TOOLBAR: Tìm kiếm & Lọc Khách hàng & Nút Xuất Gộp ═══ */}
          <div className="pt-3.5 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 w-full sm:w-auto flex-1">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Tìm theo mã FQC, LSX, Đơn hàng, Tên sản phẩm, Khách hàng..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 h-9 text-xs md:text-sm bg-white border-slate-200 rounded-lg shadow-xs"
                />
              </div>

              {customers.length > 1 && (
                <div className="flex items-center gap-1.5">
                  <select
                    value={customerFilter}
                    onChange={(e) => setCustomerFilter(e.target.value)}
                    className="h-9 px-3 text-xs bg-white border border-slate-200 rounded-lg text-slate-700 font-medium shadow-xs outline-none"
                  >
                    <option value="ALL">Tất cả khách hàng ({customers.length})</option>
                    {customers.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Bulk Action Button (Rule 9) */}
            {selectedBatchIds.length > 0 && (
              <div className="flex items-center gap-2 shrink-0 animate-in fade-in duration-200">
                <Badge className="bg-slate-900 text-white text-xs px-2.5 py-1">
                  Đã chọn {selectedBatchIds.length} lô
                </Badge>
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs h-9 px-3 shadow-md gap-1.5"
                  onClick={handleBulkCreate}
                >
                  <Truck className="h-4 w-4" />
                  Tạo Phiếu Xuất Giao ({selectedBatchIds.length}) Lô Đã Chọn
                </Button>
              </div>
            )}
          </div>
        </DialogHeader>

        {/* ═══ 3. BẢNG DỮ LIỆU RỘNG RÃI & CHI TIẾT (Expanded Data Grid) ═══ */}
        <div className="flex-1 overflow-auto p-3 md:p-4 bg-slate-100/40">
          {isLoading ? (
            <div className="py-24 text-center text-slate-500 flex flex-col items-center gap-2">
              <Loader2 className="h-9 w-9 animate-spin text-emerald-600" />
              <p className="text-sm font-medium">Đang tải danh sách lô thành phẩm đã kiểm định...</p>
            </div>
          ) : filteredBatches.length === 0 ? (
            <div className="py-20 text-center text-slate-400 flex flex-col items-center gap-2 bg-white rounded-xl border border-slate-200 p-8 shadow-xs">
              <ShieldCheck className="h-14 w-14 text-slate-300 mb-1" />
              <p className="font-bold text-slate-800 text-base">
                Không có lô FQC nào đang chờ xuất giao
              </p>
              <p className="text-xs text-slate-500 max-w-md leading-relaxed">
                Tất cả các lô thành phẩm đã nghiệm thu đều đã được giao đủ, hoặc chưa có biên bản KCS FQC nào đạt chuẩn trong hệ thống.
              </p>
            </div>
          ) : (
            <div className="rounded-xl border border-slate-200 overflow-hidden shadow-sm bg-white">
              <div className="overflow-x-auto min-w-full">
                <Table className="w-full">
                  <TableHeader className="bg-slate-100 sticky top-0 z-10 border-b border-slate-200">
                    <TableRow className="hover:bg-slate-100">
                      <TableHead className="w-9 text-center px-1">
                        <Checkbox
                          checked={allSelected}
                          onCheckedChange={toggleSelectAll}
                          aria-label="Chọn tất cả"
                          className="translate-y-[2px]"
                        />
                      </TableHead>
                      <TableHead className="w-9 text-center text-slate-500 font-mono text-[11px] px-1">
                        STT
                      </TableHead>
                      <TableHead className="w-32 whitespace-nowrap text-slate-700 font-bold uppercase tracking-wider text-[11px]">
                        Biên Bản KCS FQC
                      </TableHead>
                      <TableHead className="w-36 whitespace-nowrap text-slate-700 font-bold uppercase tracking-wider text-[11px]">
                        Đơn Hàng & LSX
                      </TableHead>
                      <TableHead className="min-w-[170px] max-w-[200px] text-slate-700 font-bold uppercase tracking-wider text-[11px]">
                        Khách Hàng (Pháp Nhân)
                      </TableHead>
                      <TableHead className="min-w-[190px] max-w-[240px] text-slate-700 font-bold uppercase tracking-wider text-[11px]">
                        Sản Phẩm & Quy Cách
                      </TableHead>
                      <TableHead className="w-28 text-right whitespace-nowrap text-emerald-800 font-bold uppercase tracking-wider text-[11px] bg-emerald-50/60">
                        Còn Cần Giao
                      </TableHead>
                      <TableHead className="w-24 text-right whitespace-nowrap text-slate-700 font-bold uppercase tracking-wider text-[11px]">
                        Thành Tiền
                      </TableHead>
                      <TableHead className="w-20 text-center whitespace-nowrap text-slate-700 font-bold uppercase tracking-wider text-[11px]">
                        Thùng
                      </TableHead>
                      <TableHead className="w-24 text-center whitespace-nowrap text-slate-700 font-bold uppercase tracking-wider text-[11px] bg-slate-100">
                        Thao Tác
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredBatches.map((batch: any, index: number) => {
                      const isSelected = selectedBatchIds.includes(batch.qcFId);
                      const estimatedBatchValue = (Number(batch.qtyRemaining) || 0) * (Number(batch.unitPrice) || 0);

                      return (
                        <TableRow
                          key={batch.qcFId || batch.poId}
                          className={cn(
                            "hover:bg-slate-50/80 transition-colors border-b border-slate-100",
                            isSelected && "bg-emerald-50/40 hover:bg-emerald-50/60"
                          )}
                        >
                          {/* Checkbox */}
                          <TableCell className="text-center">
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleSelectRow(batch.qcFId)}
                              aria-label={`Chọn lô ${batch.finalQcId}`}
                              className="translate-y-[2px]"
                            />
                          </TableCell>

                          {/* STT */}
                          <TableCell className="text-center text-xs font-mono text-slate-400">
                            {index + 1}
                          </TableCell>

                          {/* Biên Bản FQC */}
                          <TableCell>
                            <div className="flex flex-col gap-1">
                              <EntityLink
                                type="qcFinal"
                                id={batch.qcFId}
                                code={batch.finalQcId}
                                className="font-bold text-xs text-indigo-700 hover:text-indigo-900"
                              />
                              <div className="flex items-center gap-1.5 flex-wrap">
                                {batch.qcStatus === "PASSED" ? (
                                  <Badge className="bg-emerald-100 text-emerald-800 border-0 text-[10px] px-2 py-0.5 font-bold">
                                    <CheckCircle2 className="h-3 w-3 mr-1 text-emerald-700" /> ĐẠT
                                  </Badge>
                                ) : (
                                  <Badge className="bg-amber-100 text-amber-800 border-0 text-[10px] px-2 py-0.5 font-bold">
                                    <AlertTriangle className="h-3 w-3 mr-1 text-amber-700" /> CHẤP NHẬN
                                  </Badge>
                                )}
                              </div>
                              <span className="text-[11px] text-slate-500 font-medium">
                                KCS: {batch.inspectorName || "Nghiệm thu"} • {formatDate(batch.checkTime, "short")}
                              </span>
                            </div>
                          </TableCell>

                          {/* Đơn Hàng & Lệnh Sản Xuất */}
                          <TableCell>
                            <div className="flex flex-col gap-1">
                              {batch.soCode ? (
                                <div className="flex items-center gap-1">
                                  <span className="text-[10px] text-slate-400 font-semibold uppercase">ĐH:</span>
                                  <EntityLink
                                    type="order"
                                    id={batch.soId || batch.soCode}
                                    code={batch.soCode}
                                    className="font-bold text-xs text-indigo-600 hover:underline"
                                  />
                                </div>
                              ) : (
                                <span className="text-[11px] text-slate-400 italic">Đơn nội bộ / Lẻ</span>
                              )}
                              <div className="flex items-center gap-1">
                                <span className="text-[10px] text-slate-400 font-semibold uppercase">LSX:</span>
                                <EntityLink
                                  type="productionOrder"
                                  id={batch.poId}
                                  code={batch.poCode}
                                  className="text-xs font-mono font-medium text-slate-700 hover:text-slate-900"
                                />
                              </div>
                            </div>
                          </TableCell>

                          {/* Khách Hàng (Tách Cột Riêng Biệt, Rule 6) */}
                          <TableCell>
                            <div className="flex flex-col gap-1 max-w-[220px]">
                              <div className="flex items-center gap-1.5">
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-slate-50 text-slate-600 font-mono shrink-0">
                                  {batch.customerShortName || "KH"}
                                </Badge>
                                <span className="font-bold text-xs text-slate-900 leading-snug line-clamp-1" title={batch.customerName || batch.customerShortName}>
                                  {batch.customerName || batch.customerShortName}
                                </span>
                              </div>
                              {batch.deliveryAddress && (
                                <span className="text-[11px] text-slate-500 line-clamp-1 leading-tight" title={batch.deliveryAddress}>
                                  ĐC: {batch.deliveryAddress}
                                </span>
                              )}
                            </div>
                          </TableCell>

                          {/* Sản Phẩm & Quy Cách (Tách Cột Riêng Biệt) */}
                          <TableCell>
                            <div className="flex flex-col gap-1 max-w-[260px]">
                              <span className="font-semibold text-xs text-slate-900 leading-snug line-clamp-2 break-all" title={batch.productName}>
                                {batch.productName}
                              </span>
                              <div className="flex items-center gap-2 text-[11px] text-slate-500">
                                {batch.productCode && (
                                  <span className="font-mono text-slate-600 break-words whitespace-normal max-w-[140px]">
                                    Mã: {batch.productCode}
                                  </span>
                                )}
                                <span>•</span>
                                <span>ĐVT: <strong className="text-slate-700">{batch.unit || "Hộp"}</strong></span>
                              </div>
                            </div>
                          </TableCell>

                          {/* Còn Cần Giao (Kèm KCS Đạt & Đã Giao) */}
                          <TableCell className="text-right bg-emerald-50/40">
                            <div className="flex flex-col items-end gap-1">
                              <span className="inline-block px-2.5 py-1 rounded-md bg-emerald-100 text-emerald-900 font-bold text-xs font-mono tabular-nums border border-emerald-300">
                                {formatNumber(batch.qtyRemaining)} {batch.unit || "Hộp"}
                              </span>
                              <span className="text-[10px] text-slate-500 font-mono">
                                KCS: {formatNumber(batch.totalQty)} {Number(batch.deliveredQty) > 0 ? `• Đã giao: ${formatNumber(batch.deliveredQty)}` : ""}
                              </span>
                            </div>
                          </TableCell>

                          {/* Thành Tiền Dự Kiến */}
                          <TableCell className="text-right">
                            <div className="flex flex-col items-end gap-0.5">
                              <span className="text-xs font-bold text-slate-800 tabular-nums">
                                {formatMoney(estimatedBatchValue)} ₫
                              </span>
                              {batch.unitPrice > 0 && (
                                <span className="text-[10px] text-slate-400 tabular-nums">
                                  {formatMoney(batch.unitPrice)}/đvt
                                </span>
                              )}
                            </div>
                          </TableCell>

                          {/* Thùng Carton */}
                          <TableCell className="text-center">
                            {batch.cartonStats?.total > 0 ? (
                              <Badge variant="outline" className="text-[11px] bg-slate-50 text-slate-700 border-slate-200 py-1 font-mono">
                                <Box className="h-3.5 w-3.5 mr-1 text-slate-600" />
                                <strong className="text-emerald-700">{batch.cartonStats.available}</strong>/{batch.cartonStats.total} Thùng
                              </Badge>
                            ) : (
                              <span className="text-[11px] text-slate-400 italic">Chưa đóng tem</span>
                            )}
                          </TableCell>

                          {/* Thao Tác */}
                          <TableCell className="text-center bg-white group-hover:bg-slate-50 transition-colors">
                            <Button
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8 px-2.5 font-semibold shadow-xs gap-1.5 w-full whitespace-nowrap"
                              onClick={() => handleQuickCreate(batch)}
                            >
                              <Truck className="h-3.5 w-3.5" />
                              Lập phiếu
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>

        {/* ═══ 4. FOOTER: Chốt Chặn Chất Lượng & Hành Động ═══ */}
        <div className="p-3.5 md:px-6 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-slate-600 shrink-0">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0" />
            <span className="leading-snug">
              Chốt chặn chất lượng: Chỉ cho phép xuất các lô đã hoàn tất kiểm tra nghiệm thu xuất xưởng.
            </span>
          </div>

          <div className="flex items-center gap-2.5 w-full sm:w-auto justify-end">
            {selectedBatchIds.length > 0 && (
              <Button
                variant="default"
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs h-8.5 px-3.5 shadow-sm gap-1.5"
                onClick={handleBulkCreate}
              >
                <Truck className="h-3.5 w-3.5" />
                Xuất Giao ({selectedBatchIds.length}) Lô Đã Chọn
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="h-8.5 px-4 font-medium text-slate-700 hover:bg-slate-100"
            >
              Đóng
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
